import type { MemberExtractionResourceRow } from './useMemberExtractionDocuments';

const sectionLines = (
  markdown: string,
  startHeading: string,
  endHeadings: string[],
) => {
  const lines = markdown.split(/\r?\n/);
  const start = lines.findIndex(line => line.trim() === startHeading);
  if (start < 0) {
    return [];
  }
  const rest = lines.slice(start + 1);
  const end = rest.findIndex(line => endHeadings.includes(line.trim()));
  return end < 0 ? rest : rest.slice(0, end);
};

const normalizeLabel = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const pageNumberByCitation = (markdown: string) => {
  const pages = new Map<string, number>();
  let pageNumber: number | undefined;
  sectionLines(markdown, '## Page Captions and Excerpts', [
    '## Clinical Findings',
    '## Candidate Resources',
    '## Coverage',
  ]).forEach(line => {
    const trimmed = line.trim();
    const pageMatch = trimmed.match(/^### Page (\d+)$/);
    if (pageMatch) {
      pageNumber = Number(pageMatch[1]);
      return;
    }
    const citationMatch = trimmed.match(/^- Citation: `([^`]+)`$/);
    if (citationMatch && pageNumber) {
      pages.set(citationMatch[1], pageNumber);
    }
  });
  return pages;
};

const candidatePages = (markdown: string) => {
  const pages = pageNumberByCitation(markdown);
  const byName = new Map<string, number[]>();
  sectionLines(markdown, '## Candidate Resources', ['## Coverage']).forEach(
    line => {
      const trimmed = line.trim();
      if (!trimmed.startsWith('- ')) {
        return;
      }
      const body = trimmed.slice(2);
      const markerAt = body.lastIndexOf(' (resource: `');
      if (markerAt < 0) {
        return;
      }
      const name = body.slice(0, markerAt).split(':')[0]?.trim();
      const details = body.slice(markerAt + 2);
      const resourceType = details.match(/^resource: `([^`]+)`/)?.[1];
      const status = details.match(/status: `([^`]+)`/)?.[1];
      if (!name || !resourceType || status !== 'candidate') {
        return;
      }
      const citationBlock = details.match(/citations: ([^;)]*)/)?.[1] ?? '';
      const pageNumbers: number[] = [];
      const citationPattern = /`([^`]+)`/g;
      let citationMatch = citationPattern.exec(citationBlock);
      while (citationMatch) {
        const page = pages.get(citationMatch[1]);
        if (page != null) {
          pageNumbers.push(page);
        }
        citationMatch = citationPattern.exec(citationBlock);
      }
      if (pageNumbers.length === 0) {
        return;
      }
      const key = `${resourceType}\n${normalizeLabel(name)}`;
      const current = byName.get(key) ?? [];
      byName.set(key, [...current, ...pageNumbers]);
    },
  );
  return byName;
};

export const pagesForExtractionResults = (
  markdown: string | undefined,
  resources: MemberExtractionResourceRow[],
) => {
  const pages = new Map<string, number[]>();
  if (!markdown) {
    return pages;
  }
  const candidates = candidatePages(markdown);
  resources.forEach(resource => {
    const matched = candidates.get(
      `${resource.resourceType}\n${normalizeLabel(resource.title)}`,
    );
    if (!matched || matched.length === 0) {
      return;
    }
    pages.set(
      `${resource.resourceType}/${resource.id}`,
      [...new Set(matched)].sort((left, right) => left - right),
    );
  });
  return pages;
};
